/**
 * Pure utilities exposed at `ctx.helpers.*` to Script Widget snippets.
 *
 * Phase L4b — kept minimal on purpose. Adding a helper here means we now have
 * to support it forever (built-in widget IDs are a public contract per
 * LAYOUTS_PLAN.md §0.4 and the same applies to SDK surface). Customers can
 * always inline-copy a five-liner; only land things here that pay rent across
 * many snippets.
 *
 * Everything here MUST be JSON-serialisable (strings, numbers, booleans,
 * plain objects, arrays). The sandbox transports them across the
 * postMessage boundary by `JSON.stringify`/`JSON.parse`, so no functions and
 * no non-serialisable references — these are values, not callables.
 *
 * Functions on this module ARE callable (the sandbox injects them via a
 * trampoline), but the values they return must round-trip cleanly.
 */

function pad2(n) { return n < 10 ? '0' + n : '' + n; }

export const sdkHelpers = {
  // ── Date / time ────────────────────────────────────────────────────
  nowIso() { return new Date().toISOString(); },
  formatDate(value, opts) {
    if (value == null) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    if (opts && opts.format === 'date') return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
    if (opts && opts.format === 'time') return pad2(d.getHours()) + ':' + pad2(d.getMinutes());
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()) + ' ' + pad2(d.getHours()) + ':' + pad2(d.getMinutes());
  },
  daysBetween(a, b) {
    if (a == null || b == null) return null;
    const da = new Date(a), db = new Date(b);
    if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return null;
    return Math.round((db.getTime() - da.getTime()) / 86400000);
  },

  // ── Money / numbers ────────────────────────────────────────────────
  formatMoney(value, currency) {
    if (value == null || Number.isNaN(Number(value))) return '';
    const num = Number(value);
    const sym = (currency || 'USD').toUpperCase();
    return sym + ' ' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  },

  // ── Templates ──────────────────────────────────────────────────────
  template(str, vars) {
    if (typeof str !== 'string') return '';
    return str.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) => {
      const parts = key.split('.');
      let cur = vars;
      for (const p of parts) {
        if (cur == null) return '';
        cur = cur[p];
      }
      return cur == null ? '' : String(cur);
    });
  },

  // ── Diff (object-shallow + nested keys, like helpers.diff in §4.36) ─
  diff(before, after) {
    const out = { added: {}, removed: {}, changed: {} };
    const a = before && typeof before === 'object' ? before : {};
    const b = after && typeof after === 'object' ? after : {};
    for (const k of Object.keys(b)) {
      if (!(k in a)) out.added[k] = b[k];
      else if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) out.changed[k] = { from: a[k], to: b[k] };
    }
    for (const k of Object.keys(a)) if (!(k in b)) out.removed[k] = a[k];
    return out;
  },

  // ── Form-key helpers (mirrors mapCaseValuesToFormNames intent) ─────
  caseValuesToFormNames(custom) {
    const out = {};
    if (!custom || typeof custom !== 'object') return out;
    for (const [k, v] of Object.entries(custom)) {
      if (v && typeof v === 'object' && 'value' in v) out[k] = v.value;
      else out[k] = v;
    }
    return out;
  },

  // ── String helpers ─────────────────────────────────────────────────
  slugify(s) {
    if (typeof s !== 'string') return '';
    return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  },
  truncate(s, n) {
    if (typeof s !== 'string') return '';
    if (s.length <= n) return s;
    return s.slice(0, Math.max(0, n - 1)) + '…';
  },
};
